import { XMarkIcon } from '@heroicons/react/20/solid';

import type { GroupSeatData } from '../groupSubscriptionTypes';
import { computeBulkBuckets } from './groupCard.utils';

type BulkActionBarProps = {
  groupId: string;
  selectedSeats: GroupSeatData[];
  selectionCount: number;
  clearSelection: () => void;
  onBulkRemove?: (groupId: string, seatIds: string[]) => void;
  onBulkResendInvite?: (groupId: string, seatIds: string[]) => void;
  onBulkCancelInvites?: (groupId: string, seatIds: string[]) => void;
  onBulkReinvite?: (groupId: string, seatIds: string[]) => void;
};

export function BulkActionBar({
  groupId,
  selectedSeats,
  selectionCount,
  clearSelection,
  onBulkRemove,
  onBulkResendInvite,
  onBulkCancelInvites,
  onBulkReinvite,
}: BulkActionBarProps) {
  const buckets = computeBulkBuckets(selectedSeats);

  // Below md: stack selection summary above the action buttons and let the buttons
  // wrap. md+: classic 54px horizontal bar. The bar isn't a fixed height anymore
  // because the action group may wrap to two lines on the narrowest viewports.
  return (
    <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 md:h-[54px] md:flex-row md:items-center md:justify-between md:gap-0 md:py-0">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          className="w-4 h-4 border-gray-400 rounded shrink-0 !bg-wt-primary !text-wt-text-on-primary"
          checked
          onChange={clearSelection}
          aria-label="Clear selection"
        />
        <span className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 shadow-sm">
          {selectionCount} selected
          <button
            type="button"
            onClick={clearSelection}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Clear selection"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {buckets.invited.count > 0 && (
          <>
            <button
              type="button"
              onClick={() => onBulkResendInvite?.(groupId, buckets.invited.ids)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Resend {buckets.invited.count} {buckets.invited.count === 1 ? 'invite' : 'invites'}
            </button>
            <button
              type="button"
              onClick={() => onBulkCancelInvites?.(groupId, buckets.invited.ids)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 shadow-sm hover:bg-gray-50"
            >
              Cancel {buckets.invited.count} {buckets.invited.count === 1 ? 'invite' : 'invites'}
            </button>
          </>
        )}
        {buckets.active.count > 0 && (
          <button
            type="button"
            onClick={() => onBulkRemove?.(groupId, buckets.active.ids)}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 shadow-sm hover:bg-gray-50"
          >
            Remove {buckets.active.count} {buckets.active.count === 1 ? 'subscriber' : 'subscribers'}
          </button>
        )}
        {buckets.removed.count > 0 && (
          <button
            type="button"
            onClick={() => onBulkReinvite?.(groupId, buckets.removed.ids)}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 shadow-sm hover:bg-gray-50"
          >
            Send {buckets.removed.count} {buckets.removed.count === 1 ? 'invite' : 'invites'}
          </button>
        )}
      </div>
    </div>
  );
}
